import * as React from "react";
import { useDispatch } from "react-redux";
import styled, { useTheme } from "styled-components";

import { defaultValues } from "@config/defaultValues";
import { BOOKING_EXTRAS } from "@config/testIds/booking";

import { EXTRA_SERVICES_BOOKING } from "@typings/booking";
import { MODALS } from "@typings/modals";

import { InteriorWindows } from "@ui/Assets/MyIcons";
import { ModalContentService } from "@ui/Organisms";

import { useAuth } from "@hooks/useAuth";
import { useTranslate } from "@hooks/useTranslate";
import { useTelemetry } from "@hooks/useTelemetry";

import { Modal } from "@app/containers";
import { useModalStore } from "@app/hooks/useModalStore";
import { setTask } from "@app/redux/booking/booking.actions";

const config =
  defaultValues.booking.tasks[EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS];

const InteriorWindowsServiceModal = (): React.ReactElement => {
  const { hideModal } = useModalStore();
  const translate = useTranslate();
  const { elementClick } = useTelemetry();

  const { isUserAdmin } = useAuth();
  const dispatch = useDispatch();

  const [duration, setDuration] = React.useState(config.duration);

  const telemetryContext = {
    context: {
      modalTitle: translate("batmaid_pwa_booking.modal_interior_windows_title"),
    },
  };

  const dispatchSetTask = (duration: number, selected: boolean) => {
    dispatch(
      setTask({
        service: EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS,
        duration,
        selected,
      }),
    );
  };

  const cleanupOnClose = () => {
    hideModal(MODALS.BOOKING_INTERIOR_WINDOWS_SERVICE);
    setDuration(config.duration);
  };
  const theme = useTheme();

  return (
    <Modal
      testId={"interiorWindowsCleaningModal"}
      title={translate("batmaid_pwa_booking.modal_interior_windows_title")}
      id={MODALS.BOOKING_INTERIOR_WINDOWS_SERVICE}
      submitBtnColor="blue"
      submit={() => {
        elementClick(translate("batmaid_pwa_generic.button_confirm"), {
          ...telemetryContext,
          buttonId: "booking-windows-extra-service-modal-submit",
        });

        dispatchSetTask(duration, true);
        cleanupOnClose();
      }}
      close={() => {
        elementClick(translate("batmaid_pwa_generic.button_cancel"), {
          ...telemetryContext,
          buttonId: "booking-windows-extra-service-modal-cancel",
        });

        dispatchSetTask(config.duration, false);
        cleanupOnClose();
      }}
    >
      <ModalContentService
        step={isUserAdmin ? 0.25 : 0.5}
        handleOnCounterChange={(duration, action) => {
          elementClick(translate("batmaid_pwa_booking.select_duration"), {
            ...telemetryContext,
            buttonId: `booking-windows-extra-service-modal-counter-${action}`,
          });
          setDuration(duration);
        }}
        duration={config.duration}
        counterValue={config.duration}
        minDuration={config.duration}
        maxDuration={config.maxDuration}
        translations={{
          selectDurationText: translate("batmaid_pwa_booking.select_duration"),
          serviceContent1Text: translate(
            "batmaid_pwa_booking.modal_interior_windows_service_content_1",
          ),
          serviceContent2Text: translate(
            "batmaid_pwa_booking.modal_interior_windows_service_content_2",
          ),
        }}
        serviceIcon={
          <WindowWrapper>
            <InteriorWindows width={theme.icons.sizes.base_x4} />
          </WindowWrapper>
        }
        testId={BOOKING_EXTRAS.INTERIOR_WINDOWS_MODAL_CONTENT}
      />
    </Modal>
  );
};

export { InteriorWindowsServiceModal };

InteriorWindowsServiceModal.displayName = "InteriorWindowsServiceModal";

const WindowWrapper = styled.div`
  margin: ${props => props.theme.margins.half};
`;
